import { useEffect, useState, useRef } from "react";
import { VerifyHuman } from "./services/auth.service";
import claimsLogo from "./assets/claims-logo.png";

// Define the Cloudflare Turnstile script URL
const TURNSTILE_SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js";
const TURNSTILE_SCRIPT_ID = "cloudflare-turnstile-script";
const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY2;

export default function Verify({ onVerified }) {
    const [status, setStatus] = useState("pending");
    const turnstileRef = useRef(null);
    const widgetIdRef = useRef(null);
    const scriptRef = useRef(null); // Ref to hold the dynamically created script element

    // Dispose function as normal function, hoisted
    function disposeTurnstile() {
        try {
            // 1. Remove the widget
            if (widgetIdRef.current && window.turnstile) {
                window.turnstile.remove(widgetIdRef.current);
                widgetIdRef.current = null;
            }
            // 2. Remove the script tag from the DOM
            if (scriptRef.current) {
                scriptRef.current.remove();
                scriptRef.current = null;
            }
        }
        catch (err) {
            console.error("$$Error disposing Turnstile:", err);
        }
    }

    /**
     * Helper function to dynamically load the Turnstile script.
     * @returns {Promise<any>} Resolves with the window.turnstile object.
     */
    const loadTurnstileScript = () => {
        return new Promise((resolve, reject) => {
            // If the script is already loaded and window.turnstile exists, resolve immediately
            if (window.turnstile) {
                return resolve(window.turnstile);
            }

            // Check if a script tag is already being added or is present
            const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID);
            if (existingScript) {
                existingScript.addEventListener("load", () => resolve(window.turnstile), { once: true });
                existingScript.addEventListener("error", () => reject(new Error("Failed to load Turnstile script from Cloudflare.")), { once: true });
                return;
            }

            const script = document.createElement('script');
            script.id = TURNSTILE_SCRIPT_ID;
            script.src = TURNSTILE_SCRIPT_URL;
            script.async = true;
            // The fix for "Script error." - ensures detailed error reporting
            script.setAttribute('crossorigin', 'anonymous');

            script.onload = () => {
                // Store the script element in a ref for cleanup
                scriptRef.current = script;
                if (window.turnstile) {
                    resolve(window.turnstile);
                } else {
                    reject(new Error("Turnstile script loaded but 'window.turnstile' object not found."));
                }
            };

            script.onerror = () => {
                script.remove();
                reject(new Error("Failed to load Turnstile script from Cloudflare."));
            };

            document.head.appendChild(script);
        });
    };

    useEffect(() => {
        // console.log('$$siteKey', siteKey);

        try {
            if (!siteKey) {
                console.error("Turnstile site key missing!");
                setStatus("failed");
                return;
            }

            loadTurnstileScript()
                .then((turnstile) => {
                    try {
                        // FIX Check 1: Ensure the container element is ready
                        if (!turnstileRef.current) {
                            console.error("Turnstile ref is null, cannot render.");
                            setStatus("failed");
                            return;
                        }

                        // Clear previous content
                        turnstileRef.current.innerHTML = "";

                        // Render the widget
                        widgetIdRef.current = turnstile.render(turnstileRef.current, {
                            sitekey: siteKey,
                            callback: async (token) => {
                                try {
                                    const result = await VerifyHuman(token);
                                    if (result.success) {
                                        setStatus("success");
                                        onVerified(token);
                                        disposeTurnstile(); // Clean up on success
                                    } else {
                                        setStatus("failed");
                                        if (widgetIdRef.current) turnstile.reset(widgetIdRef.current);
                                    }
                                } catch (err) {
                                    console.error("Error verifying human:", err);
                                    setStatus("failed");
                                }
                            },
                            theme: "light",
                        });
                    } catch (err) {
                        console.error("Error rendering Turnstile:", err);
                        setStatus("failed");
                    }
                })
                .catch((err) => {
                    console.error("Failed to load Turnstile script:", err);
                    setStatus("failed");
                });

            // Cleanup function will run on unmount or re-run
            return () => disposeTurnstile();
        }
        catch (err) {
            console.error("$$Unexpected error in Verify component:", err);
            setStatus("failed");
            return () => disposeTurnstile(); // Also return cleanup function in catch
        }
    }, [onVerified]);


    return (
        <div
            className="d-flex justify-content-center align-items-center vh-100 p-3"
            style={{ background: "linear-gradient(135deg, #9333ea, #ec4899, #ef4444)" }}
        >
            <div
                className="card shadow-lg text-center"
                style={{ maxWidth: "500px", width: "100%", borderRadius: "1rem" }}
            >
                <div className="card-body">
                    <div className="mb-3 d-flex justify-content-center">
                        <img
                            src={claimsLogo}
                            alt="Claims Tracking Logo"
                            className="img-fluid"
                            style={{
                                maxWidth: "280px",
                                width: "80%",
                                height: "auto",
                            }}
                        />
                    </div>

                    <h4 className="fw-bold mb-3">Human Verification</h4>
                    <p className="text-muted mb-4 text-xs">BD:03062026</p>

                    {/* Turnstile widget container */}
                    <div ref={turnstileRef} className="d-flex justify-content-center mt-3"></div>

                    {/* Status messages */}
                    {status === "failed" && (
                        <p className="text-danger fw-semibold mt-2">
                            ❌ Verification failed, please try again.
                        </p>
                    )}

                    {status === "success" && (
                        <p className="text-success fw-semibold mt-2">✅ Verified! Redirecting...</p>
                    )}
                </div>
            </div>
        </div>
    );
}
